import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify-icon/react";
import DetailedList from "../../../HelperComponents/DetailedList";
import {
  getCredentialingItems,
  updateCredentialingItem,
} from "../../../Service/Services";
import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";
import MessageTile from "../../../HelperComponents/MessageTile";
import moment from "moment";

const toBoolean = (value: any): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["true", "yes", "1"].includes(value.trim().toLowerCase());
  }
  return false;
};

interface CredentialingItemsProps {
  user: any;
}

const styles = {
  container: {
    fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
    padding: "12px",
  },
  card: {
    backgroundColor: `hsl(var(--background))`,
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    padding: "20px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: `hsl(var(--primary))`,
    paddingBottom: "8px",
    borderBottom: "1px solid hsl(var(--border))",
    marginBottom: "12px",
  },
};

const CredentialingItems = (props: CredentialingItemsProps) => {
  const [loading, setLoading] = useState(false);
  const [credentialingItems, setCredentialingItems] = useState<any[]>([]);
  const [savingIds, setSavingIds] = useState<Set<string | number>>(new Set());
  const [savedIds, setSavedIds] = useState<Set<string | number>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const savedTimeoutsRef = useRef<
    Map<string | number, ReturnType<typeof setTimeout>>
  >(new Map());

  useEffect(() => {
    const timeouts = savedTimeoutsRef.current;
    return () => {
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      timeouts.clear();
    };
  }, []);

  const fetchCredentialingItems = useCallback(async () => {
    try {
      setLoading(true);
      if (!props.user?.FullName) {
        setCredentialingItems([]);
        return;
      }
      const response = await getCredentialingItems(props.user.FullName);
      const rawItems = Array.isArray(response?.data?.items)
        ? response.data.items
        : Array.isArray(response?.data?.value)
          ? response.data.value
          : Array.isArray(response?.data)
            ? response.data
            : [];

      const mapped = rawItems.map((item: any, index: number) => {
        let autoMissing = item.Auto_x0020_Found_x0020_Missing_x;
        if (typeof autoMissing === "string") {
          try {
            const parsed = JSON.parse(autoMissing);
            if (Array.isArray(parsed)) {
              autoMissing = parsed.join(", ");
            }
          } catch {
            // keep original string if not valid JSON
          }
        }

        return {
          id: item.ID ?? item.ItemID ?? index,
          insuranceCompany: item.Insurance_x0020_Company ?? "",
          credentialingType: item.Certification_x0020_Type ?? "",
          status: item.Credentialing_x0020_Status ?? "",
          submitted: item.Submitted ?? "",
          expectedCredDate: item.Expected_x0020_Cred_x0020_Date ?? "",
          approved: item.Approved ?? "",
          "Provider#": item.Provider_x0020_ID_x0023_ ?? "",
          missingItems: item.Missing_x0020_Items ?? "",
          autoFoundMissingItems: autoMissing ?? "",
          previouslyCredentialed: toBoolean(item.Previously_x0020_Credentialed),
        };
      });

      setCredentialingItems(mapped);
    } catch (error) {
      console.error("Error fetching credentialing items:", error);
    } finally {
      setLoading(false);
    }
  }, [props.user?.FullName]);

  useEffect(() => {
    if (!props.user?.FullName) return;
    fetchCredentialingItems();
  }, [props.user?.FullName, fetchCredentialingItems]);

  const handlePreviouslyCredentialedChange = useCallback(
    async (item: any, newValue: boolean) => {
      const previousValue = item.previouslyCredentialed;

      setCredentialingItems((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? { ...row, previouslyCredentialed: newValue }
            : row,
        ),
      );
      setSavingIds((prev) => new Set(prev).add(item.id));
      setErrorMessage(null);

      const existingTimeout = savedTimeoutsRef.current.get(item.id);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        savedTimeoutsRef.current.delete(item.id);
      }
      setSavedIds((prev) => {
        if (!prev.has(item.id)) return prev;
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });

      try {
        await updateCredentialingItem(item.id, {
          Previously_x0020_Credentialed: newValue,
        });

        setSavedIds((prev) => new Set(prev).add(item.id));
        const timeoutId = setTimeout(() => {
          setSavedIds((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          savedTimeoutsRef.current.delete(item.id);
        }, 4000);
        savedTimeoutsRef.current.set(item.id, timeoutId);
      } catch (error) {
        console.error("Error updating Previously Credentialed:", error);
        setCredentialingItems((prev) =>
          prev.map((row) =>
            row.id === item.id
              ? { ...row, previouslyCredentialed: previousValue }
              : row,
          ),
        );
        setErrorMessage(
          "Failed to update Previously Credentialed. Please try again.",
        );
      } finally {
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [],
  );

  const fields = [
    {
      key: "insuranceCompany",
      name: "Insurance Company(s)",
      fieldName: "insuranceCompany",
      minWidth: 150,
      maxWidth: 180,
      isResizable: true,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
    },
    {
      key: "credentialingType",
      name: "Credentialing Type",
      fieldName: "credentialingType",
      minWidth: 150,
      maxWidth: 180,
      isResizable: true,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
    },
    {
      key: "status",
      name: "Status",
      fieldName: "status",
      minWidth: 110,
      maxWidth: 130,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
    },
    {
      key: "submitted",
      name: "Submitted",
      fieldName: "submitted",
      minWidth: 100,
      maxWidth: 120,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        return item.submitted
          ? moment(item.submitted).format("MM/DD/YYYY")
          : "";
      },
    },
    {
      key: "expectedCredDate",
      name: "Expected Cred Date",
      fieldName: "expectedCredDate",
      minWidth: 140,
      maxWidth: 140,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        return item.expectedCredDate
          ? moment(item.expectedCredDate).format("MM/DD/YYYY")
          : "";
      },
    },
    {
      key: "approved",
      name: "Approved",
      fieldName: "approved",
      minWidth: 100,
      maxWidth: 120,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        return item.approved ? moment(item.approved).format("MM/DD/YYYY") : "";
      },
    },

    {
      key: "Provider#",
      name: "Provider #",
      fieldName: "Provider#",
      minWidth: 100,
      maxWidth: 120,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
    },
    {
      key: "previouslyCredentialed",
      name: "Previously Credentialed",
      fieldName: "previouslyCredentialed",
      minWidth: 150,
      maxWidth: 170,
      isResizable: true,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        const isSaving = savingIds.has(item.id);
        const isSaved = savedIds.has(item.id);
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <input
              type="checkbox"
              checked={!!item.previouslyCredentialed}
              disabled={isSaving}
              onChange={(event) =>
                handlePreviouslyCredentialedChange(item, event.target.checked)
              }
              aria-label="Previously Credentialed"
              style={{
                width: 16,
                height: 16,
                accentColor: "hsl(var(--primary))",
                cursor: isSaving ? "not-allowed" : "pointer",
              }}
            />
            {isSaving && (
              <Icon
                icon="mdi:loading"
                className="animate-spin"
                style={{ fontSize: 16, color: "hsl(var(--primary))" }}
                aria-label="Saving"
              />
            )}
            {!isSaving && isSaved && (
              <Icon
                icon="mdi:check-circle"
                style={{ fontSize: 16, color: "#107c10" }}
                aria-label="Saved"
              />
            )}
          </div>
        );
      },
    },
    {
      key: "missingItems",
      name: "Missing Items",
      fieldName: "missingItems",
      minWidth: 150,
      maxWidth: 180,
      isResizable: true,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
    },
    {
      key: "autoFoundMissingItems",
      name: "Auto Found Missing Items",
      fieldName: "autoFoundMissingItems",
      minWidth: 180,
      maxWidth: 200,
      isResizable: true,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
    },
  ];

  return (
    <div className="contentContainer">
      {loading && (
        // <div className="spinner">
        <LoadingSpinner />
        // </div>
      )}

      <div key={1} className="content">
        {!loading && (
          <>
            <h2 className="title">Credentialing Items</h2>
            <div className="contentCard">
              <div style={styles.container}>
                <div>
                  <div style={styles.card}>
                    <h3 style={styles.sectionTitle}>Credentialing Items</h3>

                    {errorMessage && (
                      <MessageTile
                        isVisible={true}
                        message={errorMessage}
                        type="error"
                      />
                    )}

                    <DetailedList
                      items={credentialingItems}
                      columns={fields}
                      listType="shimmered"
                      selectionMode="none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CredentialingItems;
